/**
 * Generates and downloads a CSV file with the specified headers and rows.
 *
 * @param {string[]} headers - An array of strings representing the column headers for the CSV.
 * @param {Array<string[]>} rows - A 2D array where each inner array represents a row of data for the CSV.
 * @param {string} [fileName="Untitled.csv"] - The name of the file to be downloaded (default is "Untitled.csv").
 *
 * @example
 * // Example usage:
 * const headers = ["Name", "Age", "City"];
 * const rows = [
 *   ["Alice", "30", "New York"],
 *   ["Bob", "25", "Los Angeles"],
 * ];
 * generateAndDownloadCSV(headers, rows, "example.csv");
 */
import { convertUTCToTime } from "./dateTime";

export const generateAndDownloadCSV = (
  headers,
  rows,
  fileName = "Untitled.csv",
) => {
  // Add headers to rows
  const csvContent = [headers, ...rows]
    .map((e) => e.join(",")) // Convert to CSV format
    .join("\n"); // Add newline for rows

  // Create blob and download link
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", fileName);

  // Open in new tab and download
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const generateIntegrationLogCSV = (integrationLogs, timezone) => {
  // Column headers
  const headers = [
    "Account",
    "Project",
    "Contract",
    "Claim",
    "Certificate",
    "Issue Details",
    "Date & Time",
  ];

  // Map JSON data into CSV rows
  const rows = integrationLogs.map((item) => {
    const additionalInfo = item.payload.additional_info;
    const account = (additionalInfo.account.name || "").replace(/,/g, "-");
    const contractGroup = (additionalInfo.contract_group?.name || "").replace(
      /,/g,
      "-",
    );
    const contract = (additionalInfo.contract?.name || "").replace(/,/g, "-");
    const claim = (additionalInfo.claim?.name || "").replace(/,/g, "-");
    const cert = (additionalInfo.claim?.name || "").replace(/,/g, "-");
    const failureInfo = (item.failure_info || "")
      .replace(/,/g, "-")
      .replace(/'/g, "-")
      .replace(/\n/g, " ")
      .replace(/\r/g, " ")
      .replace(/[^a-zA-Z0-9\s]/g, "");

    const createdAt = convertUTCToTime(
      item?.created_at,
      timezone,
      false,
      "PPpp",
    );

    return [
      account,
      contractGroup,
      contract,
      claim,
      cert,
      failureInfo,
      `"${createdAt[0]} - ${createdAt[1]}"`,
    ];
  });

  const fileDate = new Date().toISOString().split("T")[0];

  generateAndDownloadCSV(headers, rows, `Sync Report ${fileDate}`);
};
